import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react'
import { fetchFirms, createFirm, updateFirm, deleteFirm, FirmSummary } from '../../../lib/api/api'
import { formatDate } from '../../../shared/utils/format'
import FirmTab from './FirmTab'

type FormState = { name: string; code: string }
const emptyForm: FormState = { name: '', code: '' }

function errorDetail(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

export default function FirmsAdmin() {
  const { t } = useTranslation()

  const [firms, setFirms] = useState<FirmSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingFirm, setEditingFirm] = useState<FirmSummary | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [isSaving, setIsSaving] = useState(false)
  // The firm whose letterhead the super admin is editing (FirmTab with firmId)
  const [letterheadFirm, setLetterheadFirm] = useState<FirmSummary | null>(null)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError(null)
    fetchFirms()
      .then((res) => { if (active) setFirms(res.data.firms) })
      .catch(() => { if (active) setError(t('platform.firms.errors.loadFailed')) })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [reloadToken, t])

  const openCreate = () => {
    setEditingFirm(null)
    setForm(emptyForm)
    setIsFormOpen(true)
  }

  const openEdit = (firm: FirmSummary) => {
    setEditingFirm(firm)
    setForm({ name: firm.name, code: firm.code })
    setIsFormOpen(true)
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setEditingFirm(null)
    setForm(emptyForm)
  }

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.code.trim()) return
    setIsSaving(true)
    setError(null)
    try {
      if (editingFirm) {
        await updateFirm(editingFirm.id, { name: form.name, code: form.code })
      } else {
        await createFirm({ name: form.name, code: form.code })
      }
      closeForm()
      setReloadToken((v) => v + 1)
    } catch (err) {
      setError(errorDetail(err, t('platform.firms.errors.saveFailed')))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (firm: FirmSummary) => {
    if (!window.confirm(t('platform.firms.confirmDelete', { name: firm.name }))) return
    setError(null)
    try {
      await deleteFirm(firm.id)
      setFirms((prev) => prev.filter((f) => f.id !== firm.id))
    } catch (err) {
      setError(errorDetail(err, t('platform.firms.errors.deleteFailed')))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t('platform.firms.title')}</h3>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm"
        >
          <Plus size={16} />
          {t('platform.firms.new')}
        </button>
      </div>

      {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-gray-400" />
        </div>
      ) : firms.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('platform.firms.list.noResults')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">{t('platform.firms.list.name')}</th>
                <th className="py-2 pr-4">{t('platform.firms.list.code')}</th>
                <th className="py-2 pr-4">{t('platform.firms.list.members')}</th>
                <th className="py-2 pr-4">{t('platform.firms.list.created')}</th>
                <th className="py-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {firms.map((firm) => (
                <tr key={firm.id} className="border-b border-gray-100">
                  <td className="py-2 pr-4">{firm.name}</td>
                  <td className="py-2 pr-4">{firm.code}</td>
                  <td className="py-2 pr-4">{firm.member_count}</td>
                  <td className="py-2 pr-4">{formatDate(firm.created_at)}</td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setLetterheadFirm(firm)}
                        title={t('platform.firms.actions.letterhead')}
                        aria-label={t('platform.firms.actions.letterhead')}
                        className="p-1.5 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                      >
                        <FileText size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(firm)}
                        title={t('platform.firms.actions.rename')}
                        aria-label={t('platform.firms.actions.rename')}
                        className="p-1.5 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(firm)}
                        title={t('platform.firms.actions.delete')}
                        aria-label={t('platform.firms.actions.delete')}
                        className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {letterheadFirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-end p-2 border-b">
              <button
                type="button"
                onClick={() => setLetterheadFirm(null)}
                title={t('common.close')}
                aria-label={t('common.close')}
                className="p-1.5 text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <FirmTab key={letterheadFirm.id} firmId={letterheadFirm.id} />
            </div>
          </div>
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingFirm ? t('platform.firms.form.editTitle') : t('platform.firms.form.createTitle')}
              </h3>
              <button type="button" onClick={closeForm} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <label className="block text-sm">
                <span className="text-gray-600">{t('platform.firms.form.nameLabel')}</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">{t('platform.firms.form.codeLabel')}</span>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={closeForm} className="px-3 py-1.5 rounded-md border text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
